import * as React from 'react';

import { useDebounceFn, useRequest } from 'ahooks';
import { Col, Empty, Form, Input, Row, Select, Spin, Typography } from 'antd';

import {
  AntdUploadImgCrop,
  ExclamationCircleOutlinedIcon,
  InboxOutlinedIcon,
  PrimitiveTreeSelect,
  RichTextEditor,
} from 'components';
import { useCategoryAction, useCategoryValue } from 'context';
import { getBrands, getCategories, getCategory, searchTags } from 'services';
import type { SelectOption, TreeSelectSource } from 'types';
import {
  findElementByKey,
  findTreeElementByKey,
  generateDownloadFile,
  generateSelectOptions,
  generateTreeSelectData,
  inputRule,
  normalizeToString,
  PATHNAME,
} from 'utils';

const {
  QUERY_PARAM: {
    DEFAULT: {
      PAGE_NUMBER: DEFAULT_PAGE_NUMBER,
      MAX_PAGE_SIZE: DEFAULT_MAX_PAGE_SIZE,
    },
  },
} = PATHNAME;

type TagsParams = Parameters<typeof searchTags>[number];

type FieldType = Pick<
  ReturnType<typeof useCategoryValue>,
  | 'title'
  | 'parent'
  | 'relatedAttribute'
  | 'subSlug'
  | 'iconId'
  | 'description'
  | 'catalogBrand'
  | 'tags'
> &
  Record<'relatedAttributeValue', string>;

const CategoryBaseInfo = () => {
  const { data: categoriesData, loading: categoriesLoading } = useRequest(
    getCategories,
    {
      cacheKey: 'categories',
    },
  );

  const { data: brandsData, loading: brandsLoading } = useRequest(getBrands, {
    cacheKey: 'catalogBrands',
  });

  const {
    data: tagsData,
    error: tagsError,
    params: tagsParams,
    loading: isTagsLoading,
    run: searchTagsRun,
  } = useRequest(
    async ({ searchKey }: Pick<TagsParams, 'searchKey'>) =>
      await searchTags({
        searchKey,
        pageNumber: DEFAULT_PAGE_NUMBER,
        pageSize: DEFAULT_MAX_PAGE_SIZE,
      }),
    {
      defaultParams: [{ searchKey: '' }],
      debounceWait: 1000,
    },
  );

  const {
    data: categoryData,
    loading: categoryLoading,
    run: runCategory,
  } = useRequest(getCategory, {
    manual: true,
  });
  const {
    title = '',
    parent,
    relatedAttribute,
    subSlug = '',
    iconId,
    description,
    ownerType,
  } = useCategoryValue();
  const action = useCategoryAction();

  const { categories = [] } = categoriesData ?? {};
  const {
    attributes: categoryAttributes = [],
    relatedAttribute: categoryRelatedAttribute,
  } = categoryData ?? {};

  React.useEffect(() => {
    if (parent) {
      const { id } = parent;
      runCategory({ id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent]);

  const { run: debounceUpdateTitle } = useDebounceFn(
    (debouncedTitle: typeof title) => {
      action({
        type: 'UPDATE_CATEGORY_TITLE',
        payload: {
          title: debouncedTitle,
        },
      });
    },
  );
  const { run: debounceUpdateSubSlug } = useDebounceFn(
    (debouncedSubSlug: typeof subSlug) => {
      action({
        type: 'UPDATE_CATEGORY_SUB_SLUG',
        payload: {
          subSlug: debouncedSubSlug,
        },
      });
    },
  );
  const { run: debounceUpdateDescription } = useDebounceFn(
    (debouncedDescription: typeof description) => {
      action({
        type: 'UPDATE_CATEGORY_DESCRIPTION',
        payload: {
          description: debouncedDescription,
        },
      });
    },
  );

  const { brands = [] } = brandsData ?? {};
  const { total: totalTags = 0, data: tags = [] } = tagsData ?? {};
  const { searchKey: currentTagSearchKey = '' } = tagsParams.at(0) ?? {};

  const categoriesTreeData = generateTreeSelectData(
    categories,
    'id',
    'title',
    'id',
  );
  const brandsSelectOptions = generateSelectOptions(
    brands,
    'id',
    'title',
    'id',
  );
  const attributesOptions = generateSelectOptions(
    categoryAttributes,
    'id',
    'name',
    'id',
  );
  const tagsSelectOptions = generateSelectOptions(tags, 'id', 'title', 'id');

  if (!ownerType) return <React.Fragment />;

  return (
    <Row>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='عنوان'
          name='title'
          rules={[inputRule.required, inputRule.string]}
        >
          <Input
            placeholder='عنوان دسته‌بندی را وارد کنید'
            onChange={({ target: { value } }) => {
              debounceUpdateTitle(value);
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='پیوند یکتا (Slug)'
          name='subSlug'
          rules={[inputRule.required, inputRule.nonDot]}
        >
          <Input
            placeholder='پیوند یکتا دسته‌بندی را وارد کنید'
            addonAfter={parent && `/${parent.slug}`}
            classNames={{
              input: '[&:not([value=""])]:[direction:ltr]',
            }}
            onChange={({ target: { value } }) => {
              debounceUpdateSubSlug(value);
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType> label='برند کاتالوگ' name='catalogBrand'>
          <Select<SelectOption | undefined>
            allowClear
            labelInValue
            showSearch
            maxTagCount='responsive'
            placeholder='برند کاتالوگ را انتخاب کنید'
            loading={brandsLoading}
            options={brandsSelectOptions}
            filterOption={(inputValue, option) =>
              normalizeToString(option?.label)
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
            onChange={value => {
              const brandId = normalizeToString(value?.value);
              const selectedBrand = findElementByKey(brands, 'id', brandId);

              action({
                type: 'UPDATE_CATEGORY_CATALOG_BRAND',
                payload: {
                  catalogBrand: selectedBrand,
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType> label='دسته‌بندی مادر' name='parent'>
          <PrimitiveTreeSelect<TreeSelectSource | undefined>
            labelInValue
            placeholder='دسته‌بندی مادر را انتخاب کنید'
            loading={categoriesLoading}
            treeData={categoriesTreeData}
            onChange={value => {
              const parentId = normalizeToString(value?.value);
              const selectedParent = findTreeElementByKey(
                categories,
                'id',
                parentId,
              );
              const { relatedAttribute: selectedRelatedAttribute } =
                selectedParent ?? {};

              action({
                type: 'UPDATE_CATEGORY_PARENT',
                payload: {
                  parent: selectedParent,
                },
              });
              action({
                type: 'UPDATE_CATEGORY_RELATED_ATTRIBUTE',
                payload: {
                  relatedAttribute: selectedRelatedAttribute,
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType> label='فیلد مورد نظر' name='relatedAttribute'>
          <Select<SelectOption | undefined>
            allowClear
            showSearch
            labelInValue
            placeholder='فیلد مورد نظر را انتخاب کنید'
            disabled={categoryLoading || !!categoryRelatedAttribute}
            loading={categoryLoading}
            options={
              parent
                ? categoryRelatedAttribute
                  ? [
                      {
                        label: categoryRelatedAttribute.name,
                        value: categoryRelatedAttribute.id,
                      },
                    ]
                  : attributesOptions
                : undefined
            }
            filterOption={(inputValue, option) =>
              normalizeToString(option?.label)
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
            onChange={value => {
              const attributeId = normalizeToString(value?.value);
              const selectedAttribute = findElementByKey(
                categoryAttributes,
                'id',
                attributeId,
              );

              action({
                type: 'UPDATE_CATEGORY_RELATED_ATTRIBUTE',
                payload: {
                  relatedAttribute: selectedAttribute
                    ? {
                        ...selectedAttribute,
                        selectedValues: [],
                      }
                    : undefined,
                },
              });
            }}
            {...(!parent && {
              notFoundContent: (
                <Empty
                  className='my-3'
                  description='ابتدا دسته‌بندی مادر را انتخاب کنید.'
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  imageStyle={{
                    height: 'calc(var(--ant-control-height-lg) * 0.875)',
                  }}
                />
              ),
            })}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='مقدار فیلد مورد نظر'
          name='relatedAttributeValue'
        >
          <Select<string | undefined>
            allowClear
            showSearch
            placeholder='مقدار فیلد مورد نظر را انتخاب کنید'
            disabled={categoryLoading || !categoryRelatedAttribute}
            loading={categoryLoading}
            options={
              parent && categoryRelatedAttribute
                ? categoryRelatedAttribute.values.map(value => ({
                    label: value,
                    value,
                  }))
                : undefined
            }
            onChange={value => {
              action({
                type: 'UPDATE_CATEGORY_RELATED_ATTRIBUTE',
                payload: {
                  relatedAttribute: relatedAttribute
                    ? {
                        ...relatedAttribute,
                        selectedValues: value ? [value] : [],
                      }
                    : undefined,
                },
              });
            }}
            {...(!relatedAttribute && {
              notFoundContent: (
                <Empty
                  className='my-3'
                  description='ابتدا فیلد مورد نظر را انتخاب کنید.'
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  imageStyle={{
                    height: 'calc(var(--ant-control-height-lg) * 0.875)',
                  }}
                />
              ),
            })}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> label='برچسب‌ها' name='tags'>
          <Select<SelectOption[] | undefined>
            allowClear
            labelInValue
            filterOption={false}
            mode='multiple'
            placeholder='برچسب‌های دسته بندی را انتخاب کنید'
            options={tagsSelectOptions}
            notFoundContent={
              isTagsLoading ? (
                <div className='grid min-h-24 place-content-center gap-4'>
                  <Spin />
                  <Typography.Text className='text-[--ant-color-info]'>
                    در حال بارگذاری برچسب‌ها...
                  </Typography.Text>
                </div>
              ) : tagsError ? (
                <div className='grid min-h-24 content-center justify-items-center gap-4'>
                  <ExclamationCircleOutlinedIcon className='text-2xl text-[--ant-color-error]' />
                  <Typography.Text type='danger'>
                    {tagsError instanceof Error
                      ? tagsError.message
                      : String(tagsError)}
                  </Typography.Text>
                </div>
              ) : !totalTags ? (
                <div className='grid min-h-24 content-center justify-items-center gap-2.5'>
                  <InboxOutlinedIcon className='text-3xl' />
                  <Typography.Text type='secondary'>
                    <span>هیچ برچسبی با عنوان</span>
                    <span> &rdquo;{currentTagSearchKey}&rdquo; </span>
                    <span>وجود ندارد.</span>
                  </Typography.Text>
                </div>
              ) : undefined
            }
            onSearch={value => {
              const searchKey = value.trim();
              searchTagsRun({ searchKey });
            }}
            onChange={(value = []) => {
              const selectedTags = value.map(({ value, label }) => {
                const tagId = normalizeToString(value);
                const tagTitle = normalizeToString(label);
                const selectedTag = findTreeElementByKey(tags, 'id', tagId)!;

                return {
                  ...(selectedTag ?? {}),
                  id: tagId,
                  title: tagTitle,
                };
              });

              action({
                type: 'UPDATE_CATEGORY_TAGS',
                payload: {
                  tags: selectedTags,
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='نشان‌واره (Icon)'
          name='iconId'
          valuePropName='file'
        >
          <AntdUploadImgCrop
            ownerType={ownerType}
            fileList={
              iconId
                ? [
                    {
                      uid: iconId,
                      name: title,
                      status: 'done',
                      url: generateDownloadFile(ownerType, iconId, 200, 200),
                    },
                  ]
                : undefined
            }
            onChange={({ file: { status, response } }) => {
              action({
                type: 'UPDATE_CATEGORY_ICON',
                payload: {
                  iconId: undefined,
                },
              });

              if (status === 'done' && response) {
                const { id } = response;
                action({
                  type: 'UPDATE_CATEGORY_ICON',
                  payload: {
                    iconId: id,
                  },
                });
              }
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> label='توضیحات' name='description'>
          <RichTextEditor
            ownerType={ownerType}
            options={{
              content: description,
              onUpdate: ({ editor }) => {
                debounceUpdateDescription(editor.getHTML());
              },
            }}
            config={{
              placeholder: {
                placeholder: 'توضیحات دسته‌بندی را وارد کنید',
              },
            }}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export { CategoryBaseInfo };
